import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { articles } from "../../mocks/news";

const NAV_ITEMS = [
  { label: "首页", path: "/" },
  { label: "娱乐圈", path: "/category/yule" },
  { label: "网红圈", path: "/category/wanghong" },
  { label: "独家首发", path: "/category/dujia" },
  { label: "关于我们", path: "/about" },
  { label: "常见问题", path: "/faq" },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setQuery("");
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  const results = query.trim()
    ? articles
        .filter(
          (a) =>
            a.title.includes(query.trim()) ||
            a.tags.some((t) => t.includes(query.trim())) ||
            a.summary.includes(query.trim()),
        )
        .slice(0, 6)
    : [];

  const goSearch = (slug: string) => {
    setSearchOpen(false);
    setQuery("");
    navigate(`/news/${slug}`);
  };

  return (
    <>
      {/* 顶部细条 */}
      <div className="bg-primary-600 text-background-50">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5 text-xs">
          <p className="flex items-center gap-2">
            <i className="ri-flashlight-fill text-accent-300" />
            <span className="hidden sm:inline">聚焦娱乐圈 · 网红圈独家热点，首发快人一步</span>
            <span className="sm:hidden">每日爆料网 · 独家首发</span>
          </p>
          <div className="hidden items-center gap-3 md:flex">
            <span className="opacity-90">每周七天更新</span>
            <span className="opacity-50">|</span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" /> 2026年9月9日
            </span>
          </div>
        </div>
      </div>

      {/* 主导航 */}
      <header
        className={`sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur transition-shadow ${
          scrolled ? "shadow-sm" : ""
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
          {/* logo */}
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 font-serif text-lg font-black text-background-50">
              爆
            </span>
            <span className="font-serif text-xl font-black tracking-tight text-foreground-950 md:text-2xl">
              每日爆料<span className="text-primary-600">网</span>
            </span>
          </Link>

          {/* 桌面导航 */}
          <nav className="hidden items-center gap-1 md:flex">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `relative whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "text-primary-600"
                      : "text-foreground-700 hover:text-primary-600"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && (
                      <span className="absolute inset-x-3 -bottom-[2px] h-0.5 rounded-full bg-primary-500" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="搜索"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
            >
              <i className="ri-search-line text-lg" />
            </button>
            <Link
              to="/faq#tougao"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 lg:inline-flex"
            >
              线索投稿
            </Link>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="菜单"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-900 hover:bg-background-100 md:hidden"
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
            </button>
          </div>
        </div>

        {/* 移动端菜单 */}
        {menuOpen && (
          <nav className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
            <div className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium ${
                      isActive ? "bg-primary-50 text-primary-700" : "text-foreground-800 hover:bg-background-100"
                    }`
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-lg opacity-50" />
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </header>

      {/* 搜索浮层 */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60] bg-foreground-950/40 px-4 pt-20" onClick={() => setSearchOpen(false)}>
          <div
            className="mx-auto max-w-2xl overflow-hidden rounded-xl border border-background-200 bg-background-50 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 border-b border-background-200 px-4">
              <i className="ri-search-line text-lg text-foreground-500" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索你感兴趣的热点、栏目、标签…"
                className="w-full bg-transparent py-3.5 text-base outline-none placeholder:text-foreground-400"
              />
              <button
                onClick={() => setSearchOpen(false)}
                aria-label="关闭"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto p-2">
              {query.trim() && results.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-foreground-500">没有找到相关内容，换个关键词试试吧</p>
              )}
              {!query.trim() && (
                <div className="px-3 py-2">
                  <p className="mb-2 text-xs font-medium text-foreground-500">热门标签</p>
                  <div className="flex flex-wrap gap-2">
                    {["短剧", "直播", "影视", "复古", "独家", "红人经济", "算法"].map((t) => (
                      <span
                        key={t}
                        onClick={() => setQuery(t)}
                        className="cursor-pointer whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                      >
                        # {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {results.map((a) => (
                <button
                  key={a.slug}
                  onClick={() => goSearch(a.slug)}
                  className="flex w-full cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-background-100"
                >
                  <span className="mt-0.5 inline-flex shrink-0 whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                    {a.categoryName}
                  </span>
                  <span className="line-clamp-2 text-sm text-foreground-900">{a.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}